// Helpers for rendering bulk-stage and dispatcher errors. Both request-level
// (HTTP 400 envelope) and per-row stream errors share the same
// `{ code, params, message, field? }` shape so this module covers both.
//
// The audience is non-technical admins. For every code we know, we produce a
// plain-English message that explains what's wrong AND what to do. The raw
// server `message` is only used as a fallback for codes we don't recognize —
// templates can leak jargon ("rejected", "tier", "fast-path") that doesn't
// help an admin fix their CSV.

// Maps ERR_TAXONOMY_MISSING_* codes to the admin route where the user can
// create the missing entity. Mirrors TAXONOMY_GROUPS in TaxonomyErrors.jsx.
const TAXONOMY_DEEP_LINKS = {
  ERR_TAXONOMY_MISSING_CATEGORY:     { label: "Categories",     route: "/category" },
  ERR_TAXONOMY_MISSING_SUB_CATEGORY: { label: "Sub-categories", route: "/subcategory" },
  ERR_TAXONOMY_MISSING_BRAND:        { label: "Brands",         route: "/brand" },
  ERR_TAXONOMY_MISSING_OCCASION:     { label: "Occasions",      route: "/occassion" },
  ERR_TAXONOMY_MISSING_TYPE:         { label: "Types",          route: "/type" },
  ERR_TAXONOMY_MISSING_COLOR:        { label: "Colors",         route: "/color" },
  ERR_TAXONOMY_MISSING_SIZE:         { label: "Sizes",          route: "/size" },
};

export function getTaxonomyDeepLink(code) {
  return TAXONOMY_DEEP_LINKS[code] || null;
}

// `field` is optional on per-row errors; backend duplicates it inside `params`
// for template parity. Read whichever is present, prefer the top-level key.
export function getErrorField(error) {
  if (!error || typeof error !== "object") return null;
  if (error.field) return error.field;
  if (error.params && error.params.field) return error.params.field;
  return null;
}

// User-facing field label for the cell-level badge. Backend uses snake_case
// column names; admins are used to title-case ("Sub Category", "Arabic Name").
const FIELD_LABELS = {
  name: "Name",
  ar_name: "Arabic Name",
  category: "Category",
  sub_category: "Sub Category",
  ar_category: "Arabic Category",
  ar_sub_category: "Arabic Sub Category",
  brand: "Brand",
  barcode: "Barcode",
  occasion: "Occasion",
  type: "Type",
  color: "Color",
  size: "Size",
  cost: "Cost",
  regular_price: "Regular Price",
  sale_price: "Sale Price",
  quantity: "Quantity",
  description: "Description",
  ar_description: "Arabic Description",
  is_b2b: "B2B",
  product_option_type: "Product Option Type",
  has_product_option: "Has Product Option",
  status: "Status",
};

export function getFieldLabel(field) {
  if (!field) return null;
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  // Sensible default: snake_case → Title Case.
  return field
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

// Each entry takes the error's params and returns a plain-English sentence.
// Keep it short, explain both the problem and the fix, no jargon.
const ERROR_FORMATTERS = {
  ERR_REQUIRED_FIELD_MISSING: ({ field }) => {
    const label = getFieldLabel(field);
    return label
      ? `${label} is empty. Fill it in before re-uploading this row.`
      : "A required column is empty. Fill it in before re-uploading this row.";
  },

  ERR_INVALID_VALUE: ({ field, allowed, value }) => {
    const label = getFieldLabel(field) || "This field";
    if (Array.isArray(allowed) && allowed.length > 0) {
      return `${label} can only be: ${allowed.join(", ")}. You sent "${value ?? ""}".`;
    }
    if (field === "is_b2b") {
      return `B2B must be "yes" or "no". You sent "${value ?? ""}".`;
    }
    return `${label} has a value we don't accept${value != null ? ` ("${value}")` : ""}. Pick from the allowed options.`;
  },

  ERR_FORBIDDEN_FIELD: ({ field }) => {
    const label = getFieldLabel(field);
    return label
      ? `${label} is not allowed for your role. Remove the column from your CSV and re-upload.`
      : "Cost-related columns are not allowed for your role. Remove them from your CSV and re-upload.";
  },

  ERR_NUMERIC_INVALID: ({ field, value }) => {
    const label = getFieldLabel(field) || "This field";
    return `${label} must be a number. You sent "${value ?? ""}".`;
  },

  ERR_MARGIN_TOO_LOW: ({ field, margin, min_margin }) => {
    const label = getFieldLabel(field) || "Price";
    const min = min_margin ?? 30;
    if (margin != null) {
      return `${label} is only ${Number(margin).toFixed(1)}% above cost. It needs to be at least ${min}% above cost.`;
    }
    return `${label} must be at least ${min}% above cost.`;
  },

  ERR_BARCODE_INVALID_FORMAT: () =>
    "Barcode can only contain letters, numbers, dashes (-), slashes (/), or dots (.).",

  ERR_BARCODE_TOO_LONG: ({ max }) =>
    `Barcode is too long. Keep it under ${max ?? 32} characters.`,

  ERR_BARCODE_DUPLICATE_IN_BATCH: ({ barcode }) =>
    barcode
      ? `Barcode "${barcode}" appears more than once in this file. Each row needs a unique barcode.`
      : "This barcode appears more than once in this file. Each row needs a unique barcode.",

  ERR_BARCODE_ALREADY_LIVE: ({ barcode }) =>
    barcode
      ? `A live product already uses barcode "${barcode}". Pick a different one.`
      : "A live product already uses this barcode. Pick a different one.",

  ERR_BARCODE_ALREADY_STAGED: ({ barcode }) =>
    barcode
      ? `Another pending product already uses barcode "${barcode}". Pick a different one.`
      : "Another pending product already uses this barcode. Pick a different one.",

  ERR_TAXONOMY_MISSING_CATEGORY: ({ name }) =>
    name
      ? `Category "${name}" doesn't exist yet. Add it on the Categories page, then re-upload.`
      : "This category doesn't exist yet. Add it on the Categories page, then re-upload.",

  ERR_TAXONOMY_MISSING_SUB_CATEGORY: ({ name }) =>
    name
      ? `Sub-category "${name}" doesn't exist yet. Add it on the Sub-categories page, then re-upload.`
      : "This sub-category doesn't exist yet. Add it on the Sub-categories page, then re-upload.",

  ERR_TAXONOMY_MISSING_BRAND: ({ name }) =>
    name
      ? `Brand "${name}" doesn't exist yet. Add it on the Brands page, then re-upload.`
      : "This brand doesn't exist yet. Add it on the Brands page, then re-upload.",

  ERR_TAXONOMY_MISSING_OCCASION: ({ name }) =>
    name
      ? `Occasion "${name}" doesn't exist yet. Add it on the Occasions page, then re-upload.`
      : "This occasion doesn't exist yet. Add it on the Occasions page, then re-upload.",

  ERR_TAXONOMY_MISSING_TYPE: ({ name }) =>
    name
      ? `Type "${name}" doesn't exist yet. Add it on the Types page, then re-upload.`
      : "This type doesn't exist yet. Add it on the Types page, then re-upload.",

  ERR_TAXONOMY_MISSING_COLOR: ({ name }) =>
    name
      ? `Color "${name}" doesn't exist yet. Add it on the Colors page, then re-upload.`
      : "This color doesn't exist yet. Add it on the Colors page, then re-upload.",

  ERR_TAXONOMY_MISSING_SIZE: ({ name }) =>
    name
      ? `Size "${name}" doesn't exist yet. Add it on the Sizes page, then re-upload.`
      : "This size doesn't exist yet. Add it on the Sizes page, then re-upload.",

  // ERR_TYPE_MULTIPLE was removed: the importer no longer rejects a Type cell
  // holding more than one value — it keeps the first real type and drops the
  // rest. A product has exactly one type, so that cell shape doesn't occur in
  // practice. `formatError` falls back to the server message for any code
  // without a formatter, so nothing breaks if an old client sends it.

  ERR_INTEGRITY_CONFLICT: ({ field }) => {
    if (field === "barcode") {
      return "Another upload claimed this barcode at the same moment. Try again — if it keeps happening, pick a different barcode.";
    }
    return "Couldn't save this row because something else changed at the same moment. Try again.";
  },

  ERR_TOO_MANY_ROWS: ({ max, got }) =>
    `Your file has ${got ?? "too many"} rows. Split it into batches of ${max ?? 500} or fewer and re-upload.`,

  ERR_EMPTY_PAYLOAD: () =>
    "We couldn't find any product rows in this file. Make sure the header row is correct and there's at least one row of data below it.",

  ERR_INTERNAL: () =>
    "Something broke on our side while saving this row. Try again — if it keeps happening, contact support.",
};

// User-facing string for a single error. We always prefer our own friendly
// copy over the server template; the server message only shows up if we don't
// recognize the code yet.
export function formatError(error) {
  if (!error) return "Unknown error.";
  const code = error.code || "";
  const params = { ...(error.params || {}) };
  // Surface the top-level `field` to formatters as if it were a param, so
  // each formatter can take its inputs from one place.
  if (error.field && params.field == null) params.field = error.field;

  const formatter = ERROR_FORMATTERS[code];
  if (formatter) {
    try {
      return formatter(params);
    } catch (_) {
      // Defensive: if a formatter throws on unexpected params, fall through.
    }
  }

  if (error.message) return error.message;
  return code ? `Error: ${code}` : "Unknown error.";
}
